import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Target, MousePointerClick, Magnet, Filter, MailCheck, CalendarCheck, BarChart3, Repeat, TrendingUp } from "lucide-react";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BundlePricing from "@/components/sections/BundlePricing";
import { Reveal } from "@/lib/motion";
import { CATEGORIES, LEADGEN_GROUPS } from "@/data/services";

const LG = CATEGORIES.leadgen;

const FLOW = [
  { label: "Attract", icon: Target, to: "/lead-generation/seo", desc: "SEO · AI Search · Ads · Social · Branding" },
  { label: "Engage", icon: MousePointerClick, to: "/lead-generation/conversion-optimization", desc: "Website · Landing pages · Personalization" },
  { label: "Capture", icon: Magnet, to: "/lead-generation/lead-capture", desc: "Forms · Chat · Call · WhatsApp · SMS" },
  { label: "Qualify", icon: Filter, to: "/lead-generation/lead-qualification", desc: "AI scoring · Intent · Classification" },
  { label: "Nurture", icon: MailCheck, to: "/lead-generation/nurturing", desc: "Email · SMS · WhatsApp · Retargeting" },
  { label: "Convert", icon: CalendarCheck, to: "/lead-generation/appointments", desc: "Appointments · Calls · Quotes · Bookings" },
  { label: "Measure", icon: BarChart3, to: "/lead-generation/analytics", desc: "Attribution · CPL · CAC · Revenue" },
  { label: "Retain", icon: Repeat, to: "/lead-generation/reputation", desc: "CRM · Reviews · Reactivation" },
  { label: "Grow", icon: TrendingUp, to: "/lead-generation/laas", desc: "Referrals · Upsell · Repeat sales" },
];

const groupOf = (g) => LG.order.filter((slug) => LG.pages[slug].group === g);

export default function LeadGenOverview() {
  const navigate = useNavigate();
  usePageSeo(PAGE_SEO["/lead-generation"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Lead Generation", url: "/lead-generation" }]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const goAudit = () => { navigate("/"); setTimeout(() => document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth" }), 400); };

  return (
    <>
      <Navbar />
      <main className="pt-24" data-testid="leadgen-overview">
        {/* Hero */}
        <section className="relative overflow-hidden hero-bg border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
            <span className="overline">Lead Generation & Customer Acquisition</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-3 leading-[1.03] max-w-4xl">
              Turn Visitors Into Customers
            </h1>
            <p className="text-lg md:text-xl text-slate-500 mt-6 leading-relaxed max-w-3xl">
              We don't just generate traffic. WebsiteTech combines SEO, AI search, advertising, social, intelligent websites, AI assistants, lead capture, CRM, automation and analytics into one connected system that turns visitors into qualified leads, appointments, customers and repeat business.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <button onClick={goAudit} data-testid="leadgen-cta-audit" className="btn-primary rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm">
                Get a Lead Generation Audit <ArrowRight size={15} />
              </button>
              <Link to="/growth-advisor" data-testid="leadgen-cta-advisor" className="btn-outline rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm">
                <Sparkles size={15} /> Talk to an AI Growth Advisor
              </Link>
              <Link to="/growth-advisor" data-testid="leadgen-cta-build" className="rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-brand">
                Build My Lead System <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        {/* Acquisition flow */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="overline">The Customer Acquisition Engine</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mt-2">Attract → Engage → Capture → Qualify → Nurture → Convert → Measure → Retain → Grow</h2>
            <p className="text-slate-500 mt-3">Every stage is a WebsiteTech service. Click any step to explore it.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3 mt-10">
            {FLOW.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.04}>
                <Link to={s.to} data-testid={`flow-${s.label}`} className="group block h-full bg-white border border-slate-200 rounded-2xl p-4 card-shadow hover-lift text-center">
                  <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-brand"><s.icon size={18} /></span>
                  <div className="font-heading font-bold text-slate-900 mt-3 text-sm">{s.label}</div>
                  <div className="text-[11px] text-slate-400 mt-1 leading-tight">{s.desc}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Service groups */}
        <section className="bg-slate-50 border-y border-slate-100 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="overline">20 Coordinated Services</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mt-2 mb-10">One system, grouped into five stages</h2>
            <div className="space-y-10">
              {Object.keys(LEADGEN_GROUPS).map((g) => (
                <div key={g}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <h3 className="font-heading text-xl font-bold text-brand">{LEADGEN_GROUPS[g].label}</h3>
                    <span className="text-sm text-slate-400">{LEADGEN_GROUPS[g].tagline}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupOf(g).map((slug) => {
                      const p = LG.pages[slug];
                      return (
                        <Link key={slug} to={`/lead-generation/${slug}`} data-testid={`svc-${slug}`} className="group bg-white border border-slate-200 rounded-2xl p-5 card-shadow hover-lift">
                          <h4 className="font-heading text-lg font-bold text-slate-900 flex items-center justify-between">{p.navLabel} <ArrowUpRight size={17} className="text-slate-300 group-hover:text-brand transition-colors shrink-0" /></h4>
                          <p className="text-sm text-slate-500 mt-2 line-clamp-3">{p.lede}</p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Outcome band */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="relative overflow-hidden rounded-3xl bg-[#0b1220] text-white p-8 md:p-12">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-brand/30 blur-3xl rounded-full" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-200 bg-white/5 border border-blue-400/20 rounded-full px-3 py-1.5 mb-5">Traffic → Leads → Customers → Revenue</span>
              <h2 className="font-heading text-2xl md:text-4xl font-extrabold leading-tight max-w-3xl">Not sure where to start? Let our AI diagnose it for you.</h2>
              <p className="text-slate-300 mt-4 max-w-2xl">Answer a few questions and the WebsiteTech Growth Advisor recommends the exact combination of services for your business, location and goals.</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/growth-advisor" data-testid="leadgen-band-advisor" className="bg-white text-slate-900 font-semibold rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm hover:bg-slate-100 transition-colors"><Sparkles size={16} /> Get My Growth Plan</Link>
                <button onClick={goAudit} className="border border-white/25 text-white rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm hover:bg-white/10 transition-colors">Run a Lead Generation Audit <ArrowRight size={15} /></button>
              </div>
            </div>
          </div>
        </section>

        {/* Service bundles & pricing */}
        <BundlePricing defaultCurrency="USD" onSelect={() => navigate("/pricing")} />
      </main>
      <Footer />
    </>
  );
}
